import { BaseApiService } from './BaseApiService';
import { sharedTokenRefresh } from './tokenRefresh';

export interface ConfirmPaymentRequest {
    paymentIntentId: string;
}

export interface ConfirmPaymentResponse {
    success: boolean;
    paymentIntentId?: string;
    status?: string;
    paymentMethodType?: string;
    message?: string;
}

export interface PaymentMethod {
    id: string;
    type: 'card' | 'us_bank_account';
    card?: {
        brand?: string;
        last4?: string;
        expMonth?: number;
        expYear?: number;
        daysUntilExpiration?: number;
        isExpired?: boolean;
        expirationWarning?: 'expired' | 'expiring_soon' | null;
    };
    usBankAccount?: {
        bankName?: string;
        accountType?: string;
        last4?: string;
        status?: string;
        verificationMethod?: string;
    };
    isDefault: boolean;
}

export interface ListPaymentMethodsResponse {
    success: boolean;
    paymentMethods?: PaymentMethod[];
    message?: string;
}

export interface CreateSetupIntentRequest {
    teamId: string;
    paymentMethodTypes: string[];
}

export interface CreateSetupIntentResponse {
    success: boolean;
    clientSecret?: string;
    setupIntentId?: string;
    message?: string;
}

export interface VerifyAchRequest {
    paymentMethodId: string;
    teamId: string;
    /** The two deposit amounts in dollars (older verification method). */
    amounts?: number[];
    /** The 6-character code on the single deposit, e.g. SM11AA (Stripe's default). */
    descriptorCode?: string;
}

export interface VerifyAchResponse {
    success: boolean;
    paymentMethodId?: string;
    status?: string;
    message?: string;
}

export interface CreateAndConfirmPaymentIntentRequest {
    teamId: string;
    amount: number;
    paymentMethodId: string;
    paymentMethodType: string; // "card" or "us_bank_account"
    /** Optional description for the charge; passed to Stripe and shown on the charge. */
    description?: string | null;
    /** Optional next billing date (YYYY-MM-DD). When provided and payment succeeds, team's next billing date is set to this. Admin charge only. */
    nextBillingDate?: string | null;
    /** Optional note stored with the payment and sent to Stripe metadata (returned in webhooks). */
    note?: string | null;
    /** Optional receipt breakdown to persist for viewing later. */
    receiptUnitPrice?: number | null;
    receiptQuantity?: number | null;
    receiptSubtotal?: number | null;
    receiptProRatedDiscount?: number | null;
    receiptAdditionalOverage?: number | null;
    receiptTaxes?: number | null;
    receiptCardFee?: number | null;
    receiptPresetLabel?: string | null;
    receiptAttorneyNames?: string[] | null;
    /** Optional receipt: payment method type for PDF ('card' or 'us_bank_account'). */
    receiptPaymentMethodType?: string | null;
}

export interface CreateAndConfirmPaymentIntentResponse {
    success: boolean;
    /** Present on the 3D Secure and decline paths: 'requires_action' | 'failed' | ... */
    outcome?: string;
    paymentIntentId?: string;
    status?: string;
    chargeId?: string;
    teamPaymentId?: string;
    teamId?: string;
    /** Only when outcome is 'requires_action': pass to stripe.handleNextAction, then call finalizePayment. */
    clientSecret?: string;
    /** Stripe decline code when outcome is 'failed'. */
    code?: string;
    message?: string;
}

/**
 * Result of completing a server-confirmed charge.
 *
 * - `succeeded` - paid and applied.
 * - `pending_settlement` - bank debit submitted and applied; settles in a few days.
 * - `incomplete` - money taken but applying it failed. Staff are notified; do NOT pay again.
 * - `failed` - no money moved. Safe to try again.
 * - `requires_action` - the bank still wants the customer to authenticate (clientSecret set).
 */
export type PaymentCompletionOutcome = 'succeeded' | 'pending_settlement' | 'incomplete' | 'failed' | 'requires_action';

export interface TeamPaymentActionRequest {
    teamId: string;
    teamPaymentId: string;
}

export interface PaymentCompletionResponse {
    success: boolean;
    outcome?: PaymentCompletionOutcome;
    status?: string;
    paymentIntentId?: string;
    teamPaymentId?: string;
    teamId?: string;
    clientSecret?: string;
    code?: string;
    message?: string;
}

export interface OutstandingPayment {
    teamPaymentId: string;
    amount: number;
    /** 'processing' only while `unconfirmed`. */
    status: 'failed' | 'requires_action' | 'processing';
    description?: string | null;
    createdAt?: string | null;
    requiresAction: boolean;
    /** The last charge may have gone through and is being confirmed with Stripe. Not payable. */
    unconfirmed?: boolean;
    failureMessage?: string | null;
}

export interface GetOutstandingPaymentsResponse {
    success: boolean;
    payments?: OutstandingPayment[];
    message?: string;
}

export interface PayOutstandingPaymentRequest extends TeamPaymentActionRequest {
    /** Pay with this method instead of the team's default. */
    paymentMethodId?: string | null;
}

/** Request for self-service upgrade to Pro. Same shape as create-and-confirm plus billingFrequency. */
/** The server derives the next billing date for an upgrade; it is not sent. */
export interface UpgradeToProRequest extends Omit<CreateAndConfirmPaymentIntentRequest, 'nextBillingDate'> {
    /** "Annual" or "Monthly". Defaults to Annual. */
    billingFrequency?: string | null;
}

/**
 * What actually happened to an upgrade attempt.
 *
 * - `succeeded` - card charged, team is Pro.
 * - `pending_settlement` - a bank debit was submitted and the team is Pro now; the transfer
 *   settles in a few business days. Not an error, and retrying it would charge again.
 * - `failed` - no money moved. Safe to try again.
 * - `upgrade_incomplete` - payment taken, subscription not applied. Staff are notified; the user
 *   must NOT pay again.
 * - `duplicate` - an upgrade payment for this team was already submitted.
 * - `requires_action` - the bank wants 3D Secure; `clientSecret` is set. Run the challenge, then finalize.
 * - `incomplete` - finalize's name for `upgrade_incomplete`.
 */
export type UpgradeToProOutcome = 'succeeded' | 'pending_settlement' | 'failed' | 'upgrade_incomplete' | 'duplicate' | 'requires_action' | 'incomplete';

export interface UpgradeToProResponse extends CreateAndConfirmPaymentIntentResponse {
    outcome?: UpgradeToProOutcome;
    /** Whether the team's subscription level was actually written to Pro. */
    subscriptionUpdated?: boolean;
}

/** Request to pay for and reactivate an attorney beyond the team's paid-for seat count. */
export interface PayForSeatAndActivateUserRequest extends CreateAndConfirmPaymentIntentRequest {
    /** The deactivated user to reactivate once payment succeeds. */
    userId: string;
}

/**
 * What actually happened to a seat payment attempt. Same shape as UpgradeToProOutcome, plus
 * `seat_incomplete` for the payment-taken-but-not-applied case (mirrors `upgrade_incomplete`).
 */
export type PayForSeatOutcome = 'succeeded' | 'pending_settlement' | 'failed' | 'seat_incomplete' | 'duplicate' | 'requires_action' | 'incomplete';

export interface PayForSeatAndActivateUserResponse extends CreateAndConfirmPaymentIntentResponse {
    outcome?: PayForSeatOutcome;
    /** Whether the user was actually activated and attorney_count updated. */
    seatGranted?: boolean;
    /** The team's new attorney_count baseline once seatGranted is true. */
    attorneysPaidFor?: number;
}

/**
 * A server-priced charge (SubscriptionPricingService / subscriptionPricing.ts run on the API).
 * Same fields as ChargeBreakdown; the browser shows these and never recomputes them.
 */
export interface SubscriptionCharge {
    label: string;
    periodLabel: string;
    attorneyCount: number;
    listPerAttorney: number;
    listBase: number;
    creditPerAttorney: number;
    creditBase: number;
    creditLabel?: string | null;
    chargedPerAttorney: number;
    fullBasePerPeriod: number;
    proRated: boolean;
    dailyBaseRate: number;
    days: number;
    proRatedBase: number;
    additionalOverage: number;
    tax: number;
    cardFee: number;
    total: number;
}

/** What adding one attorney costs. `card` and `bankAccount` are null when no payment is required. */
export interface SeatQuote {
    paymentRequired: boolean;
    seatedAttorneys: number;
    paidSeats: number;
    seatsNeeded: number;
    preset?: string | null;
    reason?: string;
    card: SubscriptionCharge | null;
    bankAccount: SubscriptionCharge | null;
}

export interface SeatQuoteResponse {
    success: boolean;
    quote?: SeatQuote;
    message?: string;
}

export interface PayForInviteSeatRequest {
    teamId: string;
    paymentMethodId: string;
    paymentMethodType: 'card' | 'us_bank_account';
}

/**
 * - `succeeded` / `pending_settlement` - seat(s) paid; send the invite now.
 * - `requires_action` - run the 3D Secure challenge, then finalizePayment.
 * - `failed` - no money moved.
 * - `incomplete` - finalize: money taken but not applied; do NOT pay again.
 */
export interface PayForInviteSeatResponse {
    success: boolean;
    outcome?: PaymentCompletionOutcome;
    teamPaymentId?: string;
    paymentIntentId?: string;
    attorneysPaidFor?: number;
    amount?: number;
    clientSecret?: string;
    code?: string;
    message?: string;
}

/** create-user's 402 body when every paid attorney seat is in use. */
export const SEAT_PAYMENT_REQUIRED_CODE = 'SEAT_PAYMENT_REQUIRED';

/**
 * The seat quote from a create-user 402 SEAT_PAYMENT_REQUIRED error (BaseApiService puts the
 * body on `error.details`), or null for any other error.
 */
export function seatPaymentRequiredQuote(error: unknown): SeatQuote | null {
    const e = error as { status?: number; details?: { code?: string; quote?: SeatQuote } } | null;
    if (!e || e.status !== 402 || e.details?.code !== SEAT_PAYMENT_REQUIRED_CODE) return null;
    return e.details.quote ?? null;
}

export interface GetPaymentReceiptRequest {
    teamId: string;
    teamPaymentId: string;
}

export interface PaymentReceiptResponse {
    paymentId?: string;
    paymentAmount?: number;
    paidAt?: string | null;
    createdAt?: string | null;
    description?: string | null;
    note?: string | null;
    paymentStatus?: string | null;
    /** payment_status_lookup code, e.g. 'paid', 'partially_refunded'. */
    paymentStatusCode?: string | null;
    /** Total refunded so far. */
    amountRefunded?: number | null;
    /** What stays paid after refunds (zero after a lost dispute). */
    netPaid?: number | null;
    receiptUnitPrice?: number | null;
    receiptQuantity?: number | null;
    receiptSubtotal?: number | null;
    receiptProRatedDiscount?: number | null;
    receiptAdditionalOverage?: number | null;
    receiptTaxes?: number | null;
    receiptCardFee?: number | null;
    receiptPresetLabel?: string | null;
    receiptAttorneyNames?: string[] | null;
    receiptPaymentMethodType?: string | null;
    receiptNextBillingDate?: string | null;
    paidByName?: string | null;
}

export interface GetPaymentReceiptResponse {
    success: boolean;
    receipt?: PaymentReceiptResponse;
    message?: string;
}

export interface SetDefaultPaymentMethodRequest {
    teamId: string;
    paymentMethodId?: string | null; // Null or empty to remove default
}

export interface SetDefaultPaymentMethodResponse {
    success: boolean;
    message?: string;
}

/**
 * API service for payment operations
 */
export class PaymentApiService extends BaseApiService {
    constructor() {
        super(sharedTokenRefresh);
    }

    /**
     * Confirm a payment and update team payment method
     */
    async confirmPayment(request: ConfirmPaymentRequest): Promise<ConfirmPaymentResponse> {
        const response = await this.postJson('/v1/payments/confirm', request);
        return await this.handleResponse<ConfirmPaymentResponse>(response);
    }

    /**
     * List all payment methods for a team
     */
    async listPaymentMethods(teamId: string): Promise<ListPaymentMethodsResponse> {
        const response = await this.postJson('/v1/payments/list-methods', { teamId });
        return await this.handleResponse<ListPaymentMethodsResponse>(response);
    }

    /**
     * Detach a payment method from a customer
     */
    async detachPaymentMethod(paymentMethodId: string, teamId: string): Promise<{ success: boolean; message?: string }> {
        const response = await this.postJson('/v1/payments/detach-method', { paymentMethodId, teamId });
        return await this.handleResponse<{ success: boolean; message?: string }>(response);
    }

    /**
     * Create a SetupIntent for verifying payment methods without charging
     */
    async createSetupIntent(request: CreateSetupIntentRequest): Promise<CreateSetupIntentResponse> {
        const response = await this.postJson('/v1/payments/setup-intent', request);
        return await this.handleResponse<CreateSetupIntentResponse>(response);
    }

    /**
     * Verify an ACH bank account using microdeposits
     */
    async verifyAch(request: VerifyAchRequest): Promise<VerifyAchResponse> {
        const response = await this.postJson('/v1/payments/verify-ach', request);
        return await this.handleResponse<VerifyAchResponse>(response);
    }

    /**
     * Create and confirm a payment intent with a payment method (Admin/Root only)
     * This creates a team_payment record and processes the payment through Stripe
     */
    async createAndConfirmPaymentIntent(request: CreateAndConfirmPaymentIntentRequest): Promise<CreateAndConfirmPaymentIntentResponse> {
        const response = await this.postJson('/v1/payments/create-and-confirm-intent', request);
        return await this.handleResponse<CreateAndConfirmPaymentIntentResponse>(response);
    }

    /**
     * Self-service upgrade to Pro: charge and set team subscription to Pro. Requires ProSelfUpgrade feature flag.
     */
    async upgradeToPro(request: UpgradeToProRequest): Promise<UpgradeToProResponse> {
        const response = await this.postJson('/v1/payments/upgrade-to-pro', request);
        return await this.handleResponse<UpgradeToProResponse>(response);
    }

    /**
     * Pay for and reactivate an attorney whose reactivation would exceed the team's paid-for
     * seat count. Requires ProSelfUpgrade feature flag; only applies to a genuinely paying Pro
     * team (not Standard, not a trial-derived Pro).
     */
    async payForSeatAndActivateUser(request: PayForSeatAndActivateUserRequest): Promise<PayForSeatAndActivateUserResponse> {
        const response = await this.postJson('/v1/payments/pay-for-seat-and-activate-user', request);
        return await this.handleResponse<PayForSeatAndActivateUserResponse>(response);
    }

    /**
     * Whether inviting one more attorney needs a seat payment, and what it costs (server-priced).
     */
    async seatQuote(teamId: string): Promise<SeatQuoteResponse> {
        const response = await this.postJson('/v1/payments/seats/quote', { teamId });
        return await this.handleResponse<SeatQuoteResponse>(response);
    }

    /**
     * Buy the seat(s) needed to invite one more attorney. The server prices the charge from its own
     * quote. May answer requires_action; finish with finalizePayment.
     */
    async payForInviteSeat(request: PayForInviteSeatRequest): Promise<PayForInviteSeatResponse> {
        const response = await this.postJson('/v1/payments/pay-for-invite-seat', request);
        return await this.handleResponse<PayForInviteSeatResponse>(response);
    }

    /**
     * Complete a server-confirmed charge after stripe.handleNextAction (or a 3D Secure redirect).
     * The server reads the PaymentIntent from Stripe and applies the purchase.
     */
    async finalizePayment(request: TeamPaymentActionRequest): Promise<PaymentCompletionResponse> {
        const response = await this.postJson('/v1/payments/finalize', request);
        return await this.handleResponse<PaymentCompletionResponse>(response);
    }

    /**
     * Renewals the team still owes: failed, or waiting for bank authentication.
     */
    async getOutstandingPayments(teamId: string): Promise<GetOutstandingPaymentsResponse> {
        const response = await this.postJson('/v1/payments/outstanding/get', { teamId });
        return await this.handleResponse<GetOutstandingPaymentsResponse>(response);
    }

    /**
     * Pay an outstanding renewal with the customer present. May answer requires_action.
     */
    async payOutstandingPayment(request: PayOutstandingPaymentRequest): Promise<PaymentCompletionResponse> {
        const response = await this.postJson('/v1/payments/outstanding/pay', request);
        return await this.handleResponse<PaymentCompletionResponse>(response);
    }

    /**
     * Get stored receipt for a team payment (for viewing from payment history).
     */
    async getPaymentReceipt(request: GetPaymentReceiptRequest): Promise<GetPaymentReceiptResponse> {
        const response = await this.postJson('/v1/payments/get-receipt', request);
        return await this.handleResponse<GetPaymentReceiptResponse>(response);
    }

    /**
     * Generate and download PDF for a team payment receipt.
     * @returns Promise containing the PDF blob and suggested filename
     */
    async getPaymentReceiptPdf(request: GetPaymentReceiptRequest): Promise<{ blob: Blob; fileName: string }> {
        const response = await this.postJson('/v1/payments/get-receipt-pdf', request);
        if (!response.ok) {
            let message = `HTTP error! status: ${response.status}`;
            try {
                const err = await response.json();
                message = err.message || err.error || message;
            } catch {
                // ignore
            }
            throw new Error(message);
        }
        const blob = await response.blob();
        let fileName = 'calculist-receipt.pdf';
        const contentDisposition = response.headers.get('Content-Disposition');
        if (contentDisposition) {
            const match = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/) ||
                contentDisposition.match(/filename\*=UTF-8''([^;]+)/);
            if (match && match[1]) {
                fileName = match[1].replace(/^['"]|['"]$/g, '').trim();
                try {
                    fileName = decodeURIComponent(fileName);
                } catch {
                    // keep fileName as-is
                }
            }
        }
        return { blob, fileName };
    }

    /**
     * Set or remove the default payment method for a team
     * Pass null or empty string for paymentMethodId to remove the default
     */
    async setDefaultPaymentMethod(request: SetDefaultPaymentMethodRequest): Promise<SetDefaultPaymentMethodResponse> {
        const response = await this.postJson('/v1/payments/set-default-payment-method', request);
        return await this.handleResponse<SetDefaultPaymentMethodResponse>(response);
    }

}

export const paymentApiService = new PaymentApiService();
